import React, { useCallback, useEffect, useState } from "react";
import {
  AlertTriangle,
  ArrowDownCircle,
  ArrowUpCircle,
  Calendar,
  Inbox,
  Info,
  Scale,
  Wallet,
} from "lucide-react";
import { motion } from "framer-motion";
import Pagination from "../../../components/common/Pagination";
import { getIncomeExpenditure } from "../../../api/finance/incomeExpenditure";
import { deleteReceipt } from "../../../api/finance/receipts/receiptRequests";
import {
  showErrorToast,
  showSuccessToast,
} from "../../../components/ConfirmDialog";
import { confirmDelete, DELETE_ENTITY } from "../../../utils/confirmDelete";
import { formatDateVN } from "../../../utils/formatUtils";
import ReceiptCreateModal from "../receipts/ReceiptCreateModal";
import ReceiptPreviewModal from "../receipts/ReceiptPreviewModal";
import IncomeExpenditureFilter from "./IncomeExpenditureFilter";
import RecordCashModal from "./RecordCashModal";
import IncomeExpenditureRowActionMenu from "./IncomeExpenditureRowActionMenu";

const defaultDateRange = () => {
  const now = new Date();
  const firstDay = new Date(now.getFullYear(), now.getMonth(), 1)
    .toISOString()
    .slice(0, 10);
  const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0)
    .toISOString()
    .slice(0, 10);
  return { from_date: firstDay, to_date: lastDay };
};

const formatMoney = (v) =>
  Number(v || 0).toLocaleString("vi-VN", {
    maximumFractionDigits: 0,
    minimumFractionDigits: 0,
  });

const ENTRY_TYPE_LABELS = {
  advance: "Tạm ứng",
  payment: "Thanh toán",
  receipt: "Phiếu thu",
};

const ledgerItemToReceipt = (item) => ({
  id: item.id,
  receipt_code: item.code,
  receipt_date: item.entry_date,
  payer_type: item.payer_type,
  payer_name: item.counterparty,
  payer_id: item.payer_id,
  branch_id: item.branch_id,
  branch_name: item.branch_name,
  amount: item.amount,
  payment_method: item.payment_method,
  reason: item.description,
  status: item.receipt_status,
});

const IncomeExpenditurePage = () => {
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState(() => ({
    keyword: "",
    direction: "",
    branch_id: "",
    ...defaultDateRange(),
  }));
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const [summary, setSummary] = useState({
    total_in: 0,
    total_out: 0,
    net_amount: 0,
    pending_payments: 0,
    open_advance_remaining: 0,
    pending_outflow: 0,
  });
  const [alerts, setAlerts] = useState([]);
  const [ledger, setLedger] = useState([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [advanceReconciliation, setAdvanceReconciliation] = useState([]);
  const [scopeLabel, setScopeLabel] = useState("");
  const [showRecordInModal, setShowRecordInModal] = useState(false);
  const [showRecordOutModal, setShowRecordOutModal] = useState(false);
  const [showCustomerReceiptModal, setShowCustomerReceiptModal] = useState(false);
  const [editingReceipt, setEditingReceipt] = useState(null);
  const [previewReceipt, setPreviewReceipt] = useState(null);
  const [openActionId, setOpenActionId] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const res = await getIncomeExpenditure({
        ...filters,
        page: currentPage,
        page_size: pageSize,
      });
      setSummary(res?.summary || {});
      setAlerts(Array.isArray(res?.alerts) ? res.alerts : []);
      setLedger(res?.ledger?.records || []);
      setTotalRecords(Number(res?.ledger?.total || 0));
      setAdvanceReconciliation(
        Array.isArray(res?.advance_reconciliation)
          ? res.advance_reconciliation
          : []
      );
      setScopeLabel(res?.meta?.scope_label || "");
    } catch (error) {
      console.error("Load income expenditure failed:", error);
      showErrorToast(
        error?.response?.data?.message || "Không thể tải sổ quỹ thu-chi."
      );
    } finally {
      setLoading(false);
    }
  }, [filters, currentPage, pageSize]);

  const handleDeleteReceipt = async (item) => {
    const result = await confirmDelete({
      type: DELETE_ENTITY.receipt,
      name: item.code,
    });
    if (!result.isConfirmed) return;

    try {
      await deleteReceipt(item.id);
      showSuccessToast("Đã xóa phiếu thu.");
      fetchData();
    } catch (error) {
      console.error("Delete receipt failed:", error);
      showErrorToast(
        error?.response?.data?.message || "Không thể xóa phiếu thu."
      );
    }
  };

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    setCurrentPage(1);
  }, [filters]);

  return (
    <div className="p-4">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
        <div className="flex items-center gap-4">
          <div className="bg-red-700 p-2.5 rounded-2xl text-white shadow-lg shadow-red-200">
            <Scale size={24} />
          </div>
          <div>
            <h1 className="text-xl font-black text-gray-800 tracking-tight uppercase">
              Quản lý <span className="text-red-700">Thu — Chi</span>
            </h1>
            <div className="flex items-center gap-2 mt-0.5 flex-wrap">
              <span className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
              <p className="text-[11px] text-gray-500 font-bold uppercase tracking-wider">
                <span className="text-red-600">{totalRecords}</span> dòng sổ quỹ
                {scopeLabel ? ` • ${scopeLabel}` : ""}
              </p>
            </div>
          </div>
        </div>
      </div>

      <main>
        <SummaryCards summary={summary} />

        <IncomeExpenditureFilter
          onFilterChange={setFilters}
          onRefresh={fetchData}
          onCreateReceipt={() => setShowRecordInModal(true)}
          onCreatePayment={() => setShowRecordOutModal(true)}
          onCreateCustomerReceipt={() => {
            setEditingReceipt(null);
            setShowCustomerReceiptModal(true);
          }}
        />

        {alerts.length > 0 && (
          <div className="mb-6 space-y-2">
            {alerts.map((alert) => (
              <AlertBanner key={`${alert.type}-${alert.message}`} alert={alert} />
            ))}
          </div>
        )}

        {advanceReconciliation.length > 0 && (
          <section className="mb-6">
            <div className="flex items-center gap-2 mb-3">
              <Wallet size={18} className="text-amber-600" />
              <h2 className="text-sm font-black text-gray-800 uppercase tracking-wider">
                Đối soát tạm ứng chưa quyết toán
              </h2>
            </div>
            <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-x-auto">
              <table className="w-full text-left border-collapse min-w-[720px]">
                <thead>
                  <tr className="bg-gray-50/80 border-b border-gray-100">
                    <th className="px-5 py-3 text-[11px] font-black uppercase text-gray-400">
                      Mã tạm ứng
                    </th>
                    <th className="px-5 py-3 text-[11px] font-black uppercase text-gray-400">
                      Nhân viên
                    </th>
                    <th className="px-5 py-3 text-[11px] font-black uppercase text-gray-400 text-right">
                      Đã tạm ứng
                    </th>
                    <th className="px-5 py-3 text-[11px] font-black uppercase text-gray-400 text-right">
                      Đã quyết toán
                    </th>
                    <th className="px-5 py-3 text-[11px] font-black uppercase text-gray-400 text-right">
                      Còn lại
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {advanceReconciliation.map((row) => {
                    const settled =
                      Number(row.settlement_amount || 0) +
                      Number(row.payment_amount || 0);
                    return (
                      <tr key={row.id} className="hover:bg-amber-50/30">
                        <td className="px-5 py-3 text-sm font-bold text-gray-800">
                          {row.request_code}
                        </td>
                        <td className="px-5 py-3 text-sm text-gray-700">
                          {row.employee_name || "—"}
                        </td>
                        <td className="px-5 py-3 text-sm font-semibold text-gray-800 text-right">
                          {formatMoney(row.advance_amount)} ₫
                        </td>
                        <td className="px-5 py-3 text-sm text-emerald-700 text-right">
                          {formatMoney(settled)} ₫
                        </td>
                        <td className="px-5 py-3 text-sm font-black text-amber-700 text-right">
                          {formatMoney(row.remaining_amount)} ₫
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </section>
        )}

        <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
          <div className="px-5 py-4 border-b border-gray-100">
            <h2 className="text-sm font-black text-gray-800 uppercase tracking-wider">
              Sổ quỹ thu — chi
            </h2>
            <p className="text-xs text-gray-500 mt-1">
              Ghi nhận thu/chi nội bộ và phiếu thu khách hàng (học viên, trường, doanh nghiệp)
            </p>
          </div>
          <div className="overflow-x-auto relative min-h-[220px]">
            {loading && (
              <div className="absolute inset-0 bg-white/80 flex items-center justify-center z-10 rounded-xl">
                <div className="animate-spin w-10 h-10 border-2 border-red-600 border-t-transparent rounded-full" />
              </div>
            )}
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="bg-gray-50/80 border-b border-gray-100">
                  <th className="px-4 py-4 text-[11px] font-black uppercase text-gray-400 text-center w-14">
                    STT
                  </th>
                  <th className="px-5 py-4 text-[11px] font-black uppercase text-gray-400">
                    Loại
                  </th>
                  <th className="px-5 py-4 text-[11px] font-black uppercase text-gray-400">
                    Mã / Hồ sơ gốc
                  </th>
                  <th className="px-5 py-4 text-[11px] font-black uppercase text-gray-400">
                    Diễn giải
                  </th>
                  <th className="px-5 py-4 text-[11px] font-black uppercase text-gray-400 text-right">
                    Số tiền
                  </th>
                  <th className="px-5 py-4 text-[11px] font-black uppercase text-gray-400 text-center">
                    Ngày
                  </th>
                  <th className="px-5 py-4 text-[11px] font-black uppercase text-gray-400 text-right w-28">
                    Thao tác
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {!loading && ledger.length === 0 && (
                  <tr>
                    <td colSpan={7} className="px-6 py-16 text-center">
                      <Inbox
                        className="w-12 h-12 mx-auto text-gray-200 mb-3"
                        strokeWidth={1}
                      />
                      <p className="text-gray-400 text-sm font-medium italic">
                        Không có dữ liệu phù hợp bộ lọc.
                      </p>
                    </td>
                  </tr>
                )}
                {ledger.map((item, index) => (
                  <tr
                    key={`${item.entry_type}-${item.id}`}
                    className="group hover:bg-red-50/40 transition-colors"
                  >
                    <td className="px-4 py-4 text-center text-xs font-bold text-gray-400">
                      {(currentPage - 1) * pageSize + index + 1}
                    </td>
                    <td className="px-5 py-4">
                      <DirectionBadge direction={item.direction} />
                      <div className="text-[10px] text-gray-400 font-bold uppercase mt-1">
                        {ENTRY_TYPE_LABELS[item.entry_type] || item.entry_type}
                      </div>
                    </td>
                    <td className="px-5 py-4">
                      <div className="text-sm font-bold text-gray-800 group-hover:text-red-700">
                        {item.code}
                      </div>
                      <div className="text-xs text-gray-500 mt-0.5">
                        {item.linked_dossier_code || "—"}
                        {item.linked_dossier_label
                          ? ` · ${item.linked_dossier_label}`
                          : ""}
                      </div>
                      <div className="text-[10px] text-gray-400 mt-0.5">
                        {item.counterparty || "—"}
                      </div>
                    </td>
                    <td className="px-5 py-4">
                      <div
                        className="max-w-[240px] truncate text-sm text-gray-600"
                        title={item.description}
                      >
                        {item.description || "—"}
                      </div>
                      <div className="text-[10px] text-gray-400 uppercase mt-0.5">
                        {item.branch_name || "—"}
                      </div>
                    </td>
                    <td className="px-5 py-4 text-right">
                      <span
                        className={`text-sm font-black ${
                          item.direction === "in"
                            ? "text-emerald-700"
                            : "text-red-700"
                        }`}
                      >
                        {item.direction === "in" ? "+" : "−"}
                        {formatMoney(item.amount)} ₫
                      </span>
                    </td>
                    <td className="px-5 py-4">
                      <div className="inline-flex items-center gap-1.5 text-xs font-bold text-gray-600 bg-gray-50 group-hover:bg-white px-2 py-1 rounded-lg">
                        <Calendar size={12} className="text-gray-400" />
                        {item.entry_date
                          ? formatDateVN(item.entry_date)
                          : "—"}
                      </div>
                      {item.entry_type === "receipt" && item.receipt_status && (
                        <div className="mt-1 flex justify-center">
                          <ReceiptStatusBadge status={item.receipt_status} />
                        </div>
                      )}
                    </td>
                    <td className="px-5 py-4 text-right">
                      {item.entry_type === "receipt" ? (
                        <div className="flex items-center justify-end">
                          <IncomeExpenditureRowActionMenu
                            item={item}
                            isOpen={openActionId === `receipt-${item.id}`}
                            onOpenChange={(open) =>
                              setOpenActionId(open ? `receipt-${item.id}` : null)
                            }
                            onView={(row) =>
                              setPreviewReceipt(ledgerItemToReceipt(row))
                            }
                            onEdit={(row) => {
                              setEditingReceipt(ledgerItemToReceipt(row));
                              setShowCustomerReceiptModal(true);
                            }}
                            onDelete={handleDeleteReceipt}
                          />
                        </div>
                      ) : (
                        <span className="text-[10px] text-gray-300">—</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {totalRecords > 0 && (
          <Pagination
            totalRecords={totalRecords}
            currentPage={currentPage}
            pageSize={pageSize}
            onPageChange={setCurrentPage}
            onPageSizeChange={(size) => {
              setCurrentPage(1);
              setPageSize(size);
            }}
          />
        )}
      </main>

      <RecordCashModal
        isOpen={showRecordInModal}
        onClose={() => setShowRecordInModal(false)}
        direction="in"
        onSaved={fetchData}
      />

      <RecordCashModal
        isOpen={showRecordOutModal}
        onClose={() => setShowRecordOutModal(false)}
        direction="out"
        onSaved={fetchData}
      />

      <ReceiptCreateModal
        isOpen={showCustomerReceiptModal}
        onClose={() => {
          setShowCustomerReceiptModal(false);
          setEditingReceipt(null);
        }}
        receipt={editingReceipt}
        onSaved={() => {
          setShowCustomerReceiptModal(false);
          setEditingReceipt(null);
          fetchData();
        }}
      />

      <ReceiptPreviewModal
        isOpen={!!previewReceipt}
        onClose={() => setPreviewReceipt(null)}
        receipt={previewReceipt}
      />
    </div>
  );
};

const SummaryCards = ({ summary }) => {
  const items = [
    {
      label: "Tổng thu",
      sub: "Đã ghi nhận trong kỳ",
      value: `${formatMoney(summary.total_in)} ₫`,
      icon: ArrowDownCircle,
      color: "text-emerald-600",
      bg: "bg-emerald-50/50",
      border: "border-emerald-100",
    },
    {
      label: "Tổng chi",
      sub: "Đã ghi nhận trong kỳ",
      value: `${formatMoney(summary.total_out)} ₫`,
      icon: ArrowUpCircle,
      color: "text-red-600",
      bg: "bg-red-50/50",
      border: "border-red-100",
    },
    {
      label: "Chênh lệch",
      sub: "Thu − Chi trong kỳ",
      value: `${formatMoney(summary.net_amount)} ₫`,
      icon: Scale,
      color: summary.net_amount >= 0 ? "text-blue-600" : "text-rose-600",
      bg: summary.net_amount >= 0 ? "bg-blue-50/50" : "bg-rose-50/50",
      border: summary.net_amount >= 0 ? "border-blue-100" : "border-rose-100",
    },
    {
      label: "Chờ chi",
      sub: "ĐN đã duyệt, chưa ghi nhận chi",
      value: `${formatMoney(summary.pending_outflow)} ₫`,
      icon: Wallet,
      color: "text-amber-600",
      bg: "bg-amber-50/50",
      border: "border-amber-100",
    },
  ];

  return (
    <div className="flex overflow-x-auto pb-4 gap-4 snap-x no-scrollbar lg:grid lg:grid-cols-4 lg:gap-4 mb-6">
      {items.map((item) => (
        <motion.div
          key={item.label}
          whileHover={{ y: -4 }}
          className={`relative border snap-start shrink-0 ${item.border} ${item.bg} rounded-3xl p-4 lg:p-5 shadow-sm bg-white w-[75%] sm:w-[45%] lg:w-full`}
        >
          <div className="flex items-center justify-between mb-3">
            <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest">
              {item.sub}
            </p>
            <item.icon size={18} className={item.color} />
          </div>
          <p className={`text-xs font-bold ${item.color} mb-1`}>{item.label}</p>
          <p className="text-xl lg:text-2xl font-black text-gray-900 tracking-tight">
            {item.value}
          </p>
        </motion.div>
      ))}
    </div>
  );
};

const DirectionBadge = ({ direction }) =>
  direction === "in" ? (
    <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-black uppercase tracking-wider border bg-emerald-50 text-emerald-700 border-emerald-100">
      <ArrowDownCircle size={12} /> Thu
    </span>
  ) : (
    <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-black uppercase tracking-wider border bg-red-50 text-red-700 border-red-100">
      <ArrowUpCircle size={12} /> Chi
    </span>
  );

const AlertBanner = ({ alert }) => {
  const styles = {
    danger: "bg-red-50 border-red-200 text-red-800",
    warning: "bg-amber-50 border-amber-200 text-amber-900",
    info: "bg-blue-50 border-blue-200 text-blue-900",
  };
  const Icon = alert.severity === "info" ? Info : AlertTriangle;

  return (
    <div
      className={`flex items-start gap-3 px-4 py-3 rounded-xl border text-sm font-medium ${
        styles[alert.severity] || styles.info
      }`}
    >
      <Icon size={18} className="shrink-0 mt-0.5" />
      <span>{alert.message}</span>
    </div>
  );
};

const ReceiptStatusBadge = ({ status }) => {
  if (status === "posted") {
    return (
      <span className="px-2 py-0.5 rounded-full text-[9px] font-black uppercase tracking-wider border bg-emerald-50 text-emerald-700 border-emerald-100">
        Đã ghi nhận
      </span>
    );
  }
  if (status === "draft") {
    return (
      <span className="px-2 py-0.5 rounded-full text-[9px] font-black uppercase tracking-wider border bg-amber-50 text-amber-700 border-amber-100">
        Nháp
      </span>
    );
  }
  return (
    <span className="px-2 py-0.5 rounded-full text-[9px] font-black uppercase tracking-wider border bg-red-50 text-red-700 border-red-100">
      Hủy
    </span>
  );
};

export default IncomeExpenditurePage;
